import { useMemo } from "react";
import { useAuth } from "@/store/AuthStore";
import { useApp } from "@/store/AppStore";
import type { AdvisorMovement, Distributor, Product, Sale } from "@/types";

// Datos derivados del asesor autenticado (su consignación, su margen y sus ventas).
export interface AsesorItem {
  product: Product;
  units: number; // total disponible (consignado + propio)
  consignedUnits: number; // unidades en consignación (se deben al vender)
  ownedUnits: number; // unidades compradas (ya son suyas)
  costAdvisor: number; // precio costo asesor (según su margen)
  pricePublic: number;
  margin: number;
  profit: number; // ganancia por unidad
}

interface UseAsesorResult {
  advisorId: string;
  advisor: Distributor | undefined;
  name: string;
  margin: number;
  items: AsesorItem[];
  units: number;
  consignedUnits: number;
  ownedUnits: number;
  inventoryValue: number;
  potentialProfit: number;
  compradoUnits: number;
  compradoValue: number;
  consignadoValue: number;
  soldConsignedUnits: number;
  soldConsignedValue: number;
  salesTotal: number;
  profitRealized: number;
  debt: number;
  movements: AdvisorMovement[];
  sales: Sale[];
}

export const useAsesor = (): UseAsesorResult => {
  const { profile, user } = useAuth();
  const { data } = useApp();

  const advisorId = profile?.id ?? "";
  const advisor = data.distributors.find((d) => d.id === advisorId);
  const margin = advisor?.margin ?? profile?.margin ?? 25;

  const items = useMemo<AsesorItem[]>(() => {
    const list: AsesorItem[] = [];
    data.inventory.forEach((inv) => {
      const consigned = inv.stockByAdvisor[advisorId] ?? 0;
      const owned = inv.stockOwnedByAdvisor?.[advisorId] ?? 0;
      const units = consigned + owned;
      if (units <= 0) return;
      const product = data.products.find((p) => p.id === inv.productId);
      if (!product) return;
      const costAdvisor = Math.round(product.pricePublic * (1 - margin / 100));
      list.push({
        product,
        units,
        consignedUnits: consigned,
        ownedUnits: owned,
        costAdvisor,
        pricePublic: product.pricePublic,
        margin,
        profit: product.pricePublic - costAdvisor,
      });
    });
    return list;
  }, [data.inventory, data.products, advisorId, margin]);

  const sales = useMemo(
    () => data.sales.filter((s) => s.advisorId === advisorId),
    [data.sales, advisorId]
  );

  const movements = useMemo(
    () => data.advisorMovements.filter((m) => m.advisorId === advisorId),
    [data.advisorMovements, advisorId]
  );

  const units = items.reduce((acc, i) => acc + i.units, 0);
  const consignedUnits = items.reduce((acc, i) => acc + i.consignedUnits, 0);
  const ownedUnits = items.reduce((acc, i) => acc + i.ownedUnits, 0);
  const inventoryValue = items.reduce((acc, i) => acc + i.units * i.pricePublic, 0);
  const potentialProfit = items.reduce((acc, i) => acc + i.units * i.profit, 0);
  const consignadoValue = items.reduce((acc, i) => acc + i.consignedUnits * i.costAdvisor, 0);

  // Compras (mercancía que el asesor le compró al admin).
  const compradoUnits = movements
    .filter((m) => m.kind === "compra")
    .reduce((acc, m) => acc + m.qty, 0);
  const compradoValue = movements
    .filter((m) => m.kind === "compra")
    .reduce((acc, m) => acc + m.amount, 0);

  // Deuda con el admin: compras + ventas de consignación, menos sus abonos.
  const charges = movements
    .filter((m) => m.kind === "compra" || m.kind === "venta" || m.kind === "fee")
    .reduce((acc, m) => acc + m.amount, 0);
  const payments = movements
    .filter((m) => m.kind === "pago")
    .reduce((acc, m) => acc + m.amount, 0);
  const debt = Math.max(0, charges - payments);

  // Ventas realizadas: unidades que el asesor colocó a clientes saliendo de la
  // consignación del admin (movimientos de tipo "venta").
  const soldConsignedUnits = movements
    .filter((m) => m.kind === "venta")
    .reduce((acc, m) => acc + m.qty, 0);
  const soldConsignedValue = movements
    .filter((m) => m.kind === "venta")
    .reduce((acc, m) => acc + m.amount, 0);

  // Ventas totales y ganancia realizada (a su costo de asesor, según su %).
  const salesTotal = sales.reduce((acc, s) => acc + s.total, 0);
  const profitRealized = sales.reduce((acc, s) => {
    const product = data.products.find((p) => p.id === s.productId);
    const unitCost = product ? Math.round(product.pricePublic * (1 - margin / 100)) : 0;
    return acc + (s.total - unitCost * s.qty);
  }, 0);

  const fullName =
    [profile?.names, profile?.surnames].filter(Boolean).join(" ") ||
    user?.email?.split("@")[0] ||
    "Asesor";

  return {
    advisorId,
    advisor,
    name: fullName,
    margin,
    items,
    units,
    consignedUnits,
    ownedUnits,
    inventoryValue,
    potentialProfit,
    compradoUnits,
    compradoValue,
    consignadoValue,
    soldConsignedUnits,
    soldConsignedValue,
    salesTotal,
    profitRealized,
    debt,
    movements,
    sales,
  };
};